// Api
import { ApolloClient } from '@apollo/client';
import { GET_MEMORABILIA_BY_ID } from 'api/memorabilia/queries';
import { GET_STREAM_REDUCED } from 'api/streams/queries';
import { GET_MEDIA_POST_REDUCED } from 'api/mediaPost/queries';
import { GET_STORE_MERCH_PRODUCT } from 'api/merch/queries';
import { GET_MY_CAMPAIGN_OFFER_REDUCED } from 'api/campaignV2/queries';
// Types
import { GetNotifications_getNotifications_data } from 'api/notifications/types/GetNotifications';
import { GetUnreadNotifications_getUnreadNotifications_data } from 'api/notifications/types/GetUnreadNotifications';
import { GetReadNotifications_getReadNotifications_data } from 'api/notifications/types/GetReadNotifications';
import { NotificationActionEnum } from 'api/graphql-notification-global-types';
import {
  GetStreamReduced,
  GetStreamReduced_stream,
} from 'api/streams/types/GetStreamReduced';
import {
  GetMediaPostReduced,
  GetMediaPostReduced_getMediaPost,
} from 'api/mediaPost/types/GetMediaPostReduced';
import {
  GetMemorabiliaById,
  GetMemorabiliaById_getMemorabiliaById,
} from 'api/memorabilia/types/GetMemorabiliaById';
import {
  GetStoreMerchProduct,
  GetStoreMerchProduct_getStoreMerchProduct,
} from 'api/merch/types/GetStoreMerchProduct';
import {
  GetMyCampaignOfferReduced,
  GetMyCampaignOfferReduced_getMyCampaignOffer,
} from 'api/campaignV2/types/GetMyCampaignOfferReduced';
// Constants
import {
  ACCOUNT_PRODUCTS,
  DASHBOARD,
  MANAGE_AMA,
  MANAGE_MERCH,
  MANAGE_STREAMS,
  MEDIA_LIBRARY,
  USER_SCHEDULE_INTERVIEW,
  SCHEDULE_INTERVIEW,
} from 'constants/routes';
import { MY_SALES_TABS } from 'constants/mySales';

const HIDDEN_NOTIFICATION_ACTIONS: Set<NotificationActionEnum> = new Set([
  NotificationActionEnum.InterviewNowStartAction,
]);

export const filterHiddenNotifications = (
  notifications: IncomingNotificationData[]
): IncomingNotificationData[] =>
  notifications.filter(
    (n) =>
      !HIDDEN_NOTIFICATION_ACTIONS.has(
        n.action?.action as NotificationActionEnum
      )
  );

const getIsMemorabilia = (actionObject: NotificationAction): boolean => {
  const result =
    actionObject.action === NotificationActionEnum.ProductAthleteView;

  return result;
};

const getIsStream = (actionObject: NotificationAction): boolean => {
  const result =
    actionObject.action === NotificationActionEnum.InterviewRescheduleAction ||
    actionObject.action === NotificationActionEnum.StreamJoinAction ||
    actionObject.action === NotificationActionEnum.StreamShareAction ||
    actionObject.action === NotificationActionEnum.StreamStartAction ||
    actionObject.action === NotificationActionEnum.StreamWatchAction ||
    actionObject.action === NotificationActionEnum.StreamWatchPastAction ||
    actionObject.action ===
      NotificationActionEnum.PpvStreamHighlightsWatchAction;

  return result;
};

// subtype of stream, so we have same ENUM in getIsStream and here
const getIsCohostInterviewStream = (
  actionObject: NotificationAction
): boolean => {
  const result =
    actionObject.action === NotificationActionEnum.StreamJoinAction;

  return result;
};

// subtype of stream, where we get streamId but need to extract interviewId to proceed
const getIsInterviewReschedule = (
  actionObject: NotificationAction
): boolean => {
  const result =
    actionObject.action === NotificationActionEnum.InterviewRescheduleAction;

  return result;
};

// possible action for stream that need redirects(in case of a payed stream that wasn't bought)
const getIsStreamWatchAction = (actionObject: NotificationAction) => {
  const result =
    actionObject.action === NotificationActionEnum.StreamWatchAction ||
    actionObject.action === NotificationActionEnum.StreamWatchPastAction;

  return result;
};

const getIsMediaFeed = (actionObject: NotificationAction): boolean => {
  const result =
    actionObject.action === NotificationActionEnum.MediaFeedShareAction ||
    actionObject.action === NotificationActionEnum.MediaFeedView;

  return result;
};

const getIsMerch = (actionObject: NotificationAction): boolean => {
  const result =
    actionObject.action === NotificationActionEnum.MerchAthleteView;

  return result;
};

const getIsCampaign = (actionObject: NotificationAction): boolean => {
  const result =
    actionObject.action === NotificationActionEnum.CampaignOfferApplyAction ||
    actionObject.action === NotificationActionEnum.CampaignOfferPublishAction ||
    actionObject.action === NotificationActionEnum.CampaignOfferViewAction;

  return result;
};

const getIsAthleteView = (actionObject: NotificationAction): boolean => {
  const result =
    actionObject.action === NotificationActionEnum.MediaFeedAthleteView;

  return result;
};

export type IncomingNotificationData =
  | GetNotifications_getNotifications_data
  | GetUnreadNotifications_getUnreadNotifications_data
  | GetReadNotifications_getReadNotifications_data;

export type NotificationAction = {
  action: NotificationActionEnum;
  actionId: string | null;
};

export type NotificationData = {
  id: string;
  userId: string;
  title: string;
  body: string | null;
  imageUrl: string | null;
  category: string | null;
  action: NotificationAction | null;
  createdAt: any;
  isRead: boolean;
};

export type ExpandedNotificationData = NotificationData & {
  ownerSlug?: string;
  item?: string;
  redirectUrl?: string;
};

const getIsStreamNotBought = (stream: GetStreamReduced): boolean => {
  if (!stream.stream.isFree && !stream.stream.isPurchased) {
    // if stream is not free and is not purchased
    return true;
  }
  return false;
};

const getShouldRedirectToStreamDetails = (
  action: NotificationAction,
  stream?: GetStreamReduced
): boolean => {
  const isStreamWatchAction = getIsStreamWatchAction(action);
  const isStreamNotBought = !!stream && getIsStreamNotBought(stream);
  const isStreamHidden = stream?.stream.isHidden;

  return isStreamWatchAction && isStreamNotBought && !isStreamHidden;
};

// after mapping data, we fetch individual action data like stream/memorabilia/merch by ID in this function
// so we have a proper redirect URL (these use different info, like ID or slug or some flag)
export const enrichData = async (
  notifications: IncomingNotificationData[],
  client: ApolloClient<any>
): Promise<ExpandedNotificationData[]> => {
  const result: ExpandedNotificationData[] = [];

  for (const item of notifications) {
    const {
      id,
      userId,
      title,
      body,
      imageUrl,
      category,
      action,
      createdAt,
      isRead,
    } = item;

    // initiating 3 parameters we need for redirect urls
    let ownerSlug: string | undefined;
    let itemSlug: string | undefined;
    let redirectUrl: string | undefined;

    try {
      if (action?.action && action?.actionId) {
        let memorabiliaData: GetMemorabiliaById | undefined,
          streamData: GetStreamReduced | undefined,
          mediaPostData: GetMediaPostReduced | undefined,
          merchData: GetStoreMerchProduct | undefined,
          campaignData: GetMyCampaignOfferReduced | undefined;

        const isMemorabilia = getIsMemorabilia(action);
        const isStream = getIsStream(action);
        const isMediaPost = getIsMediaFeed(action);
        const isMerch = getIsMerch(action);
        const isCampaign = getIsCampaign(action);

        if (isMemorabilia) {
          const { data } = await client.query({
            query: GET_MEMORABILIA_BY_ID,
            variables: { id: action.actionId },
            fetchPolicy: 'cache-first',
          });
          memorabiliaData = data;
        } else if (isStream) {
          const { data } = await client.query({
            query: GET_STREAM_REDUCED,
            variables: { input: { id: action.actionId } },
            fetchPolicy: 'cache-first',
          });
          streamData = data;
        } else if (isMediaPost) {
          const { data } = await client.query({
            query: GET_MEDIA_POST_REDUCED,
            variables: { input: { id: action.actionId } },
            fetchPolicy: 'cache-first',
          });
          mediaPostData = data;
        } else if (isMerch) {
          const { data } = await client.query({
            query: GET_STORE_MERCH_PRODUCT,
            variables: { input: { slug: action.actionId } },
            fetchPolicy: 'cache-first',
          });
          merchData = data;
        } else if (isCampaign) {
          const { data } = await client.query({
            query: GET_MY_CAMPAIGN_OFFER_REDUCED,
            variables: { input: { offerId: action.actionId || '' } },
            fetchPolicy: 'cache-first',
          });
          campaignData = data;
        }

        const meta = getOwnerSlugAndItem(
          action,
          memorabiliaData,
          streamData,
          mediaPostData,
          merchData,
          campaignData
        );

        ownerSlug = meta.ownerSlug;
        itemSlug = meta.item;

        const shouldRedirectToStreamDetails = getShouldRedirectToStreamDetails(
          action,
          streamData
        );

        if (shouldRedirectToStreamDetails) {
          // special case when user tries going to payed stream which they didn't buy
          redirectUrl = `/${ownerSlug}/streams/${streamData?.stream.slug}`;
        } else
          redirectUrl = getActionRedirectUrl(
            action.action,
            itemSlug,
            ownerSlug || ''
          );
      }
    } catch (err) {
      // Uncomment for debugging
      // console.error(`Enrichment failed for notification ID: ${id}`, err);
    }

    // fallback for non-enriched data
    if (!redirectUrl && action?.action && action?.actionId) {
      itemSlug = action.actionId;
      redirectUrl = getActionRedirectUrl(action.action, itemSlug);
    }
    // fallback for non-enriched data without actionId
    if (!redirectUrl && action?.action) {
      redirectUrl = getActionRedirectUrl(action.action, '');
    }

    result.push({
      id,
      userId,
      title,
      body,
      imageUrl,
      category,
      action,
      createdAt,
      isRead,
      ownerSlug,
      item: itemSlug,
      redirectUrl,
    });
  }

  return result;
};

const actionRedirectMap: Record<
  NotificationActionEnum,
  (item: string, ownerSlug?: string) => string
> = {
  // go to personal video requests in My Sales
  [NotificationActionEnum.AmaRequestFulfillmentAction]: () =>
    `/my-sales?tab=${MY_SALES_TABS.PERSONAL_VIDEO_REQUESTS}`,

  // AppUpdateAction should be only for mobile
  [NotificationActionEnum.AppUpdateAction]: () => '/',

  // actionId is "{storeSlug}/{articleSlug}"
  [NotificationActionEnum.ArticleViewAction]: (item) => {
    const [storeSlug, articleSlug] = item.split('/');
    return storeSlug && articleSlug ? `/${storeSlug}/news/${articleSlug}` : '/';
  },

  // go to specific campaign details page
  [NotificationActionEnum.CampaignOfferApplyAction]: (item) =>
    `${MANAGE_AMA}/campaigns/${item}`,

  [NotificationActionEnum.CampaignOfferViewAction]: (item) =>
    `${MANAGE_AMA}/campaigns/${item}`,

  [NotificationActionEnum.BrandDealsListViewAction]: () => `${MANAGE_AMA}`,

  // go to specific campaign details page
  [NotificationActionEnum.CampaignOfferPublishAction]: (item) =>
    `${MANAGE_AMA}/campaigns/${item}`,

  // go to channel page
  [NotificationActionEnum.ChannelStartAction]: () => MANAGE_STREAMS,

  // go to the interview reschedule page to pick new time
  [NotificationActionEnum.InterviewRescheduleAction]: (item) =>
    `${USER_SCHEDULE_INTERVIEW}/interview/${item}`,

  // go to schedule an interview page with specific interviewer
  [NotificationActionEnum.InterviewScheduleAction]: (item) =>
    `${SCHEDULE_INTERVIEW}/${item}`,

  // go to specific media post's page
  [NotificationActionEnum.MediaFeedAthleteView]: (_item, ownerSlug) =>
    `/${ownerSlug}/media`,

  // go to specific media post page
  [NotificationActionEnum.MediaFeedShareAction]: (item, ownerSlug) =>
    `/${ownerSlug}/social-media-post/${item}`,

  // go to specific media post page
  [NotificationActionEnum.MediaFeedView]: (item, ownerSlug) =>
    `/${ownerSlug}/social-media-post/${item}`,

  // go to all merch page
  [NotificationActionEnum.MerchAddAction]: () => MANAGE_MERCH,

  // go to specific merch's details page
  [NotificationActionEnum.MerchAthleteView]: (item, ownerSlug) =>
    `/${ownerSlug}/merch/${item}`,

  // go to all merch page (there is a promote/share button on each item)
  [NotificationActionEnum.MerchTemplateAction]: () => MANAGE_MERCH,

  // go to all memorabilia/product creation page
  [NotificationActionEnum.ProductAddAction]: () => `${ACCOUNT_PRODUCTS}/create`,

  // go to specific memorabilia/product's page
  [NotificationActionEnum.ProductAthleteView]: (item, ownerSlug) =>
    `/${ownerSlug}/memorabilia/${item}`,

  // go to the page where memorabilia/products are listed (there is promote button for unsold ones)
  [NotificationActionEnum.ProductTemplateAction]: () => ACCOUNT_PRODUCTS,

  // go to the page where memorabilia/products are listed (there is promote button for unsold ones)
  [NotificationActionEnum.ProductTemplateLatestAction]: () => ACCOUNT_PRODUCTS,

  // go to the page where memorabilia/products are listed (there is promote button for unsold ones)
  [NotificationActionEnum.ShareLatestMemorabiliaAction]: () => ACCOUNT_PRODUCTS,

  // go to the specific stream's join page as guest with cohost token
  [NotificationActionEnum.StreamJoinAction]: (item) => `/guest/${item}`,

  // go to the specific stream's details page from where you can share it
  [NotificationActionEnum.StreamShareAction]: (item, ownerSlug) =>
    `/${ownerSlug}/streams/${item}`,

  // go to the specific stream's start page
  [NotificationActionEnum.StreamStartAction]: (item, ownerSlug) =>
    `/${ownerSlug}/start-stream/${item}`,

  // go to the specific stream's watch page
  [NotificationActionEnum.StreamWatchAction]: (item, ownerSlug) =>
    `/${ownerSlug}/watch-stream/video/${item}`,

  // go to the specific stream's watch page
  [NotificationActionEnum.StreamWatchPastAction]: (item, ownerSlug) =>
    `/${ownerSlug}/watch-stream/video/${item}`,

  // AI highlight clips render in the Highlights tab of the stream page, fed by the
  // public stream.ppvHighlights field - see StreamContentTabs' StreamContentTabKey
  [NotificationActionEnum.PpvStreamHighlightsWatchAction]: (item, ownerSlug) =>
    `/${ownerSlug}/streams/${item}?tab=highlights`,

  // go to the media post library where review clips are displayed at the top
  [NotificationActionEnum.VideoLabClipReviewAction]: () => MEDIA_LIBRARY,

  // go to the media post library where interview clips are displayed at the top
  [NotificationActionEnum.InterviewNowClipsReviewAction]: () => MEDIA_LIBRARY,

  // go to the media post library where post AI clips are displayed at the top
  [NotificationActionEnum.PostAiClipsReviewAction]: () => MEDIA_LIBRARY,

  // Kept for notifications delivered before PPV clips became auto-approved.
  [NotificationActionEnum.PpvStreamClipsReviewAction]: () => MEDIA_LIBRARY,

  // interview cancelled - redirect to dashboard (just an update notification)
  [NotificationActionEnum.InterviewCancelAction]: () => DASHBOARD,

  [NotificationActionEnum.InterviewNowStartAction]: () => DASHBOARD,

  // interview cancelled - redirect to dashboard (just an update notification)
  [NotificationActionEnum.TipReceivedAction]: () =>
    `/my-sales?tab=${MY_SALES_TABS.MY_TIPS}`,

  // tip payment visible - add query parameter to current page
  [NotificationActionEnum.TipPaymentVisibleAction]: (actionId) =>
    `CURRENT_PAGE?tipSuccess=true&tipId=${actionId}`,

  // redirect to dashboard action
  [NotificationActionEnum.DashboardViewAction]: () => DASHBOARD,
};

const getActionRedirectUrl = (
  actionName: NotificationActionEnum,
  item: string,
  ownerSlug?: string
): string => {
  const resolver = actionRedirectMap[actionName];
  return resolver ? resolver(item, ownerSlug) : '/';
};

const determineType = (actionObject: NotificationAction | null): string => {
  if (!actionObject) return 'default';
  if (getIsMemorabilia(actionObject)) return 'memorabilia';
  if (getIsStream(actionObject)) return 'stream';
  if (getIsMediaFeed(actionObject)) return 'mediaPost';
  if (getIsMerch(actionObject)) return 'merch';
  if (getIsCampaign(actionObject)) return 'campaign';
  if (getIsAthleteView(actionObject)) return 'athleteView';
  return 'default';
};

const getStreamItem = (
  actionObject: NotificationAction | null,
  streamData?: GetStreamReduced
): string => {
  if (!actionObject) {
    return '';
  }

  // if it's interview guest, return cohost token
  const isCohostInterviewStream = getIsCohostInterviewStream(actionObject);
  if (isCohostInterviewStream) {
    const cohostItem = streamData?.stream.interview?.streamCohostToken || '';
    return cohostItem;
  }

  // if it's interview reschedule, return interview ID
  const isInterviewReschedule = getIsInterviewReschedule(actionObject);
  if (isInterviewReschedule) {
    const interviewItem = streamData?.stream.interview?.id || '';

    return interviewItem;
  }

  // default return for non-interview streams
  return streamData?.stream?.slug ?? '';
};

type GetOwnerSlugAndItemResult = {
  // owner slug is necessary for some pages url
  ownerSlug: string;
  // last part of the url-it can be an ID, slug or a simple page flag(memorabilia/products) depending on action type
  item: string;
};

// if we have any data, we extract slugs, ids and flags where needed
const getOwnerSlugAndItem = (
  actionObject: NotificationAction | null,
  memorabiliaData?: GetMemorabiliaById,
  streamData?: GetStreamReduced,
  mediaPostData?: GetMediaPostReduced,
  merchData?: GetStoreMerchProduct,
  campaignData?: GetMyCampaignOfferReduced
): GetOwnerSlugAndItemResult => {
  if (!actionObject) {
    return { ownerSlug: '', item: '' };
  }

  let mainObj:
    | GetMemorabiliaById_getMemorabiliaById
    | GetStreamReduced_stream
    | GetMediaPostReduced_getMediaPost
    | GetStoreMerchProduct_getStoreMerchProduct
    | GetMyCampaignOfferReduced_getMyCampaignOffer
    | null;
  let item = '';

  const type = determineType(actionObject);
  switch (type) {
    case 'memorabilia':
      mainObj = memorabiliaData ? memorabiliaData.getMemorabiliaById : null;
      item = mainObj?.slug ?? '';
      break;
    case 'stream':
      mainObj = streamData ? streamData.stream : null;
      item = getStreamItem(actionObject, streamData);
      break;
    case 'mediaPost':
      mainObj = mediaPostData ? mediaPostData.getMediaPost : null;
      item = mainObj?.slug ?? '';
      break;
    case 'merch':
      mainObj = merchData ? merchData.getStoreMerchProduct : null;
      item = mainObj?.slug ?? '';
      break;
    case 'campaign':
      mainObj = campaignData ? campaignData.getMyCampaignOffer : null;
      item = mainObj?.campaign.id ?? '';
      break;
    case 'athleteView':
      return { ownerSlug: actionObject.actionId ?? '', item: '' };
    default:
      return {
        ownerSlug: '',
        item: actionObject.actionId ?? '',
      };
  }

  let ownerSlug = '';
  // GetMyCampaignOffer query doesn't have store (we already have user's data)
  if (
    mainObj &&
    'store' in mainObj &&
    typeof mainObj.store?.slug === 'string'
  ) {
    ownerSlug = mainObj.store.slug;
  }

  return {
    ownerSlug,
    item,
  };
};

export const shortenTimeString = (timeString: string): string => {
  if (!timeString) return '';

  let result = timeString.toLowerCase().trim();

  if (result === 'a few seconds ago') {
    return 'just now';
  }

  result = result
    .replace(/\bseconds?\b/g, 's')
    .replace(/\bminutes?\b/g, 'm')
    .replace(/\bhours?\b/g, 'h')
    .replace(/\bdays?\b/g, 'd')
    .replace(/\bmonths?\b/g, 'mo')
    .replace(/\byears?\b/g, 'y')
    .replace(/\bago\b/g, '') // remove "ago"
    .trim();

  // Replace 'a' or 'an' with '1
  result = result.replace(/\b(a|an)\b/g, '1');

  // Remove spaces between number and unit ("2 mo" => "2mo")
  result = result.replace(/(\d+)\s+([a-z]+)/g, '$1$2');

  return result;
};
